import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Typography, Card } from 'antd';
// Types
import {
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFileItemV2,
} from 'api/campaignV2/types/GetCampaignOffers';
import {
  CbDeliverableItemV2Status,
  DeliverableReviewAction,
} from 'api/graphql-global-types';
// Components
import {
  ALREADY_REVIEWED_STATUSES,
  DeliverableReviewData,
} from '../../TimelineDeliverablesReviewForm';
import ReviewButtons from '../ReviewButtons/ReviewButtons';
// Styles
import styles from './ContentReviewSection.module.scss';

const { Text, Title } = Typography;

type ContentReviewSectionProps = {
  contentItems: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items[];
  onRejectClick?: (
    item: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items
  ) => void;
};

// Helper function to get the display key, preferring videoTranscodingJob.resultKey
const getDisplayKey = (
  file: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items
): string | null => {
  // Check if this is a file item with videoTranscodingJob
  if ('key' in file) {
    const fileItem = file as any; // Type will be updated after GraphQL types are regenerated
    if (fileItem.videoTranscodingJob?.resultKey) {
      return fileItem.videoTranscodingJob.resultKey;
    }
    return fileItem.key || null;
  }
  return null;
};

const FilePreview = ({
  file,
}: {
  file: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items;
}) => {
  const displayKey = getDisplayKey(file);
  const isImage = displayKey?.match(/\.(jpeg|jpg|png|gif|webp)$/i);
  const isVideo = displayKey?.match(/\.(mp4|mov|avi|webm)$/i);

  // Type guard function to check if file has url property
  const hasUrl = (
    file: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items
  ): file is GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFileItemV2 => {
    return 'url' in file;
  };

  // Get display name - use name if available, otherwise use id
  const displayName = 'name' in file ? file.name : file.id;

  // Get display URL - backend should provide converted file URL in file.url when videoTranscodingJob exists
  const displayUrl = hasUrl(file) ? file.url || undefined : undefined;

  return (
    <div className={styles.previewWrapper}>
      <p className={styles.fileName}>{file.id}</p>
      {isImage ? (
        <img src={displayUrl} alt={displayName} className={styles.preview} />
      ) : isVideo ? (
        <video
          src={displayUrl}
          controls
          className={styles.preview}
          crossOrigin="anonymous"
        >
          <source src={displayUrl} />
          <track kind="captions" src="" label="No captions available" />
          Your browser does not support the video tag.
        </video>
      ) : (
        <p>Unsupported file type</p>
      )}
    </div>
  );
};

const ContentReviewSection: React.FC<ContentReviewSectionProps> = ({
  contentItems,
  onRejectClick,
}) => {
  const methods = useFormContext<DeliverableReviewData>();

  const handleReview = (itemId: string, action: DeliverableReviewAction) => {
    const currentReviews = methods.getValues('contentReviews');
    const existingIndex = currentReviews.findIndex(
      (review) => review.deliverableItemId === itemId
    );

    if (existingIndex >= 0) {
      // Toggle action - if already selected, remove action
      if (currentReviews[existingIndex].action === action) {
        currentReviews[existingIndex].action = null;
        currentReviews[existingIndex].feedback = '';
      } else {
        currentReviews[existingIndex].action = action;
      }
      methods.setValue('contentReviews', [...currentReviews]);
      if (currentReviews[existingIndex].action) {
        methods.clearErrors('contentReviews');
      }
    } else {
      // Add new review
      methods.setValue('contentReviews', [
        ...currentReviews,
        { deliverableItemId: itemId, action: action },
      ]);
      methods.clearErrors('contentReviews');
    }
  };

  const getItemAction = (itemId: string) => {
    const reviews = methods.watch('contentReviews');
    const review = reviews.find((r) => r.deliverableItemId === itemId);
    return review?.action || null;
  };

  const renderContentItem = (
    item: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items
  ) => {
    const alreadyReviewed = ALREADY_REVIEWED_STATUSES.includes(item.status);
    const shouldShow = item.status !== CbDeliverableItemV2Status.NOT_SUBMITTED;

    if (!shouldShow) return null;

    const isApproved =
      getItemAction(item.id) === DeliverableReviewAction.APPROVE;
    const isRejected =
      getItemAction(item.id) === DeliverableReviewAction.REJECT;

    return (
      <Card key={item.id} className={styles.contentCard}>
        <div className={styles.mediaHeader}>
          <Title level={5} className={styles.mediaTitle}>
            {item.task?.title || 'Content Item'}
          </Title>
          <Text type="secondary" className={styles.statusText}>
            Status: {item.status}
          </Text>
        </div>

        <div className={styles.mediaContainer}>
          <FilePreview file={item} />
        </div>

        <div className={styles.actionButtons}>
          <ReviewButtons
            isApproved={isApproved}
            isRejected={isRejected}
            handleApprove={() =>
              !alreadyReviewed &&
              handleReview(item.id, DeliverableReviewAction.APPROVE)
            }
            handleReject={() =>
              !alreadyReviewed &&
              handleReview(item.id, DeliverableReviewAction.REJECT)
            }
            alreadyReviewed={alreadyReviewed}
            onRejectClick={() => onRejectClick?.(item)}
            item={item}
          />
        </div>
      </Card>
    );
  };

  return (
    <div className={styles.contentSection}>
      <div className={styles.sectionHeader}>
        <Title level={4}>Content</Title>
        <Text type="secondary">
          {
            contentItems.filter(
              (item) => item.status !== CbDeliverableItemV2Status.NOT_SUBMITTED
            ).length
          }{' '}
          items
        </Text>
      </div>

      <div className={styles.contentGrid}>
        {contentItems.length > 0 ? (
          contentItems.map(renderContentItem)
        ) : (
          <div className={styles.noContent}>
            <Text type="secondary">No content items available</Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentReviewSection;
